import {
  MAX_PLAYERS,
  Msg,
  SNAPSHOT_HZ,
  VOICE_SELF,
  decodeCommands,
  decodeControl,
  decodeSnapshot,
  decodeVoice,
  encodeCommands,
  encodeControl,
  encodeSnapshot,
  kindOf,
  stampVoice,
  type Command,
  type Control,
  type NetEvent,
  type RosterEntry,
  type Snapshot,
  type Team,
} from './protocol';
import type { PeerId, Transport } from './transport';

/**
 * THE GLUE.
 *
 * `Transport` knows how to move bytes and nothing about the game. `Session`
 * knows the game and nothing about bytes. This is the only file that knows both,
 * and it is deliberately the smallest of the three: everything it does is
 * translate, schedule, and route.
 *
 * WHY IT TALKS TO AN INTERFACE, NOT TO `Session`. The type below is structural
 * and lives here. `Session` satisfies it without knowing this file exists, which
 * keeps the dependency pointing one way — and, more usefully, it means the whole
 * of the netcode can be tested against a thirty-line fake session with no WebGL,
 * no WASM physics and no DOM. A netcode layer that can only be exercised by
 * standing up two browsers is a netcode layer that is not really tested.
 *
 * THE TWO RATES. Commands go up at 30Hz and snapshots come down at 20Hz, and
 * neither is the 60Hz simulation rate. A command packet carries every tick since
 * the last acknowledgement rather than one tick, so the send rate has nothing to
 * do with how many ticks survive — dropping to 10Hz on a bad line costs latency
 * and not a single input. That is the whole reason the sequence numbers exist.
 *
 * WHO IS AUTHORITATIVE ABOUT WHAT. The host owns the world: guards, loot, locks,
 * the clock, and where everybody actually is. Each client owns exactly one thing,
 * its own aim, because a hundred milliseconds of latency applied to a mouse is
 * unplayable and applied to anything else is invisible.
 */

/** The part of the session this layer touches. `Session` implements it. */
export interface NetSession {
  readonly role: 'solo' | 'host' | 'client';
  readonly selfId: string;
  /** Client: commands sent and not yet acknowledged, oldest first. */
  readonly pending: readonly Command[];
  /** Client: `kind:id` of whatever the crosshair is on. */
  readonly targetKey: string | null;
  addPlayer(id: string, slot: number, name: string, team: Team): unknown;
  removePlayer(id: string): void;
  acceptCommands(from: string, cmds: readonly Command[]): void;
  acceptTarget(from: string, id: string | null): void;
  acceptAction(from: string, action: string): void;
  drainActionsOut(): string[];
  /** Encoded speech captured since the last pump, oldest first. */
  drainVoice(): ArrayBuffer[];
  /** Somebody else's speech, already relayed by the host. */
  acceptVoice(from: string, data: ArrayBuffer): void;
  buildSnapshot(tick: number): Snapshot;
  applySnapshot(s: Snapshot): void;
  drainNet(): NetEvent[];
  applyNetEvent(e: NetEvent): void;
}

/** Bumped whenever the wire changes shape. Mismatched builds are turned away. */
export const PROTOCOL_VERSION = 1;

const CMD_HZ = 30;
const PING_SEC = 1;

export interface GameNetOptions {
  /** What to call this machine's player in the lobby. */
  name: string;
  /**
   * Client only: the host has accepted us and told us who we are. The session
   * cannot be built before this fires, because a player's id and slot are
   * assigned by the host and half the session is keyed on them.
   */
  onWelcome?: (w: { you: PeerId; slot: number; roster: RosterEntry[] }) => void;
  onRoster?: (roster: readonly RosterEntry[]) => void;
  /** The host has opened the doors. Fires on the host too, so there is one path. */
  onStart?: () => void;
  onError?: (message: string) => void;
  /** A voice frame from somebody, already relayed. See `sendVoice`. */
  onVoice?: (from: PeerId, data: ArrayBuffer) => void;
}

export class GameNet {
  readonly transport: Transport;
  /** Everyone in the lobby, host first. The lobby UI renders this directly. */
  readonly roster: RosterEntry[] = [];
  /** Round-trip time to the host, or to each client if we are the host. */
  readonly rtt = new Map<PeerId, number>();
  /** Host ticks since the session started. Stamped on every snapshot. */
  tick = 0;

  private session: NetSession | null = null;
  private readonly opts: GameNetOptions;
  private snapAcc = 0;
  private cmdAcc = 0;
  private pingAcc = 0;
  private lastTargetKey: string | null | undefined;
  /** Reused by the host's decoder, so a packet from eight clients allocates once. */
  private readonly inCmds: Command[] = [];
  /** Messages that arrived before there was a session to give them to. */
  private readonly early: Array<{ from: PeerId; data: ArrayBuffer | string }> = [];
  private closed = false;
  /** Client: who the host is, learned from the roster. See `hostId`. */
  private host: PeerId = '';

  constructor(transport: Transport, opts: GameNetOptions) {
    this.transport = transport;
    this.opts = opts;

    transport.onJoin = (id) => this.onJoin(id);
    transport.onLeave = (id, reason) => this.onLeave(id, reason);
    transport.onMessage = (from, data) => this.onMessage(from, data);
    transport.onError = (m) => this.opts.onError?.(m);

    if (transport.isHost) {
      this.roster.push({
        id: transport.self,
        slot: 0,
        name: opts.name,
        team: 'thief',
        ready: true,
        ping: 0,
        host: true,
      });
    } else if (transport.open) {
      // The host is already reachable, so introduce ourselves now. When it is
      // not, `onJoin` fires for the host and does it there instead.
      this.hello();
    }
  }

  /**
   * Hand over the session.
   *
   * Separate from the constructor because of the client's chicken-and-egg: the
   * session needs the id the host assigns, and the id arrives over a transport
   * that must already be listening. Anything that lands in between is queued
   * here and replayed in order, so a snapshot that beats the welcome by a frame
   * is not lost.
   */
  attach(session: NetSession): void {
    this.session = session;
    // Anyone already in the room when we arrived. The roster was adopted before
    // the session existed, so nobody in it has a body yet — including the host,
    // who is the one player a client is guaranteed to have missed.
    for (const r of this.roster) {
      if (r.id !== session.selfId) session.addPlayer(r.id, r.slot, r.name, r.team);
    }
    if (!this.early.length) return;
    const queued = this.early.splice(0, this.early.length);
    for (const m of queued) this.onMessage(m.from, m.data);
  }

  /** Call once per rendered frame, after the session has stepped. */
  update(dt: number): void {
    const s = this.session;
    if (!s || this.closed || !this.transport.open) return;

    this.pingAcc += dt;
    if (this.pingAcc >= PING_SEC) {
      this.pingAcc = 0;
      this.control({ t: 'ping', at: now() });
    }

    // Before the host/client split, because speech is the one thing on this wire
    // that both ends send and neither end is authoritative about. Holding a
    // frame back for a tick would be twenty milliseconds added to every word.
    for (const v of s.drainVoice()) this.sendVoice(v);

    if (this.transport.isHost) {
      // Events first. A client that is told "the vitrine is open" in the same
      // frame it is told where everybody is will draw one consistent world; the
      // other order draws a thief standing inside a shutter for one frame.
      for (const e of s.drainNet()) this.control({ t: 'event', e });
      this.snapAcc += dt;
      const step = 1 / SNAPSHOT_HZ;
      if (this.snapAcc >= step) {
        // Clamped rather than looped. Falling behind on snapshots must not
        // produce a burst of near-identical ones — the newest is the only one
        // with any information in it.
        this.snapAcc = Math.min(this.snapAcc - step, step);
        this.tick += Math.round(60 / SNAPSHOT_HZ);
        if (this.transport.peers.length) {
          this.transport.broadcast(encodeSnapshot(s.buildSnapshot(this.tick)));
        }
      }
      return;
    }

    for (const a of s.drainActionsOut()) this.control({ t: 'act', action: a });
    // Aim is sent only when it changes. It changes when the crosshair crosses
    // onto a different fixture, which is a few times a minute, not thirty times
    // a second — and the host needs it before the hold completes, not before the
    // hold starts, so there is plenty of slack.
    if (s.targetKey !== this.lastTargetKey) {
      this.lastTargetKey = s.targetKey;
      this.control({ t: 'act', action: 'target', target: s.targetKey ?? undefined });
    }

    this.cmdAcc += dt;
    const step = 1 / CMD_HZ;
    if (this.cmdAcc >= step) {
      this.cmdAcc = Math.min(this.cmdAcc - step, step);
      if (s.pending.length) this.send(this.hostId(), encodeCommands(s.pending));
    }
  }

  /**
   * A lobby choice — name, team, readiness.
   *
   * On the host it is a fact and is published immediately. On a client it is a
   * request, and the answer arrives as a roster a few milliseconds later. The
   * client never edits its own entry, which is what stops two machines
   * disagreeing about who is a guard when the round starts.
   */
  setLobby(change: { name?: string; team?: Team; ready?: boolean }): void {
    if (this.closed) return;
    if (!this.transport.isHost) {
      this.control({ t: 'lobby', ...change });
      return;
    }
    const e = this.roster.find((r) => r.id === this.transport.self);
    if (!e) return;
    if (change.name) e.name = change.name.slice(0, 18);
    if (change.team) e.team = change.team;
    if (change.ready !== undefined) e.ready = change.ready;
    this.publishRoster();
  }

  /** Host: open the doors. Everybody drops the lobby and the round begins. */
  start(): void {
    if (this.closed || !this.transport.isHost) return;
    this.control({ t: 'lobby', start: true });
    this.opts.onStart?.();
  }

  /**
   * A frame of encoded voice.
   *
   * Relayed by the host rather than sent peer-to-peer, because the transport
   * only promises that everybody can reach the host — Steam's relay gives full
   * mesh, a WebSocket relay does not, and proximity voice that works in one
   * build and not the other is worse than none. The cost is one extra hop, which
   * is inaudible next to the codec's own frame size.
   */
  sendVoice(data: ArrayBuffer): void {
    if (this.closed || !this.transport.open) return;
    if (this.transport.isHost) this.transport.broadcast(data);
    else this.send(this.hostId(), data);
  }

  /**
   * Who said it, as opposed to who handed it over.
   *
   * Null when a stamped slot belongs to nobody we know about, which happens for
   * the handful of frames between somebody joining and the roster reaching us.
   * Dropping those is right: a voice at the wrong body is worse than a voice
   * missing two syllables, because the second one is over in twenty
   * milliseconds and the first makes players distrust their ears all round.
   */
  private speaker(from: PeerId, buf: ArrayBuffer): PeerId | null {
    const slot = decodeVoice(buf)?.slot ?? VOICE_SELF;
    if (slot === VOICE_SELF) return from;
    return this.roster.find((r) => r.slot === slot)?.id ?? null;
  }

  close(): void {
    this.closed = true;
    this.transport.close();
  }

  // --- receiving ---------------------------------------------------------

  private onMessage(from: PeerId, data: ArrayBuffer | string): void {
    if (this.closed) return;
    const kind = kindOf(data);

    // Voice is relayed before anything else looks at it, and without a session:
    // a spectator with no body still has a mouth.
    if (kind === Msg.Voice) {
      const buf = data as ArrayBuffer;
      if (this.transport.isHost) {
        // Stamped once, on the way out, with who actually spoke — the receivers
        // are about to see it arrive from us and would otherwise believe it.
        const slot = this.roster.find((r) => r.id === from)?.slot ?? VOICE_SELF;
        const relay = stampVoice(buf, slot);
        for (const p of this.transport.peers) if (p !== from) this.send(p, relay);
      }
      const who = this.speaker(from, buf);
      if (who) {
        this.session?.acceptVoice(who, buf);
        this.opts.onVoice?.(who, buf);
      }
      return;
    }

    if (!this.session) {
      // The welcome is the one message that must be handled before there is a
      // session, since it is what makes building one possible.
      if (kind === 0) {
        const c = decodeControl(data as string);
        if (c && c.t === 'welcome') {
          this.applyWelcome(c);
          return;
        }
      }
      if (this.early.length < 256) this.early.push({ from, data });
      return;
    }

    switch (kind) {
      case Msg.Commands:
        if (this.transport.isHost) {
          this.session.acceptCommands(from, decodeCommands(data as ArrayBuffer, this.inCmds));
        }
        break;
      case Msg.Snapshot:
        if (!this.transport.isHost) this.session.applySnapshot(decodeSnapshot(data as ArrayBuffer));
        break;
      case 0: {
        const c = decodeControl(data as string);
        if (c) this.onControl(from, c);
        break;
      }
      default:
        break;
    }
  }

  private onControl(from: PeerId, c: Control): void {
    const s = this.session;
    switch (c.t) {
      case 'hello':
        if (!this.transport.isHost) break;
        if (c.version !== PROTOCOL_VERSION) {
          this.send(from, encodeControl({ t: 'event', e: versionToast(c.version) }));
          break;
        }
        this.admit(from, c.name);
        break;
      case 'welcome':
        this.applyWelcome(c);
        break;
      case 'roster':
        this.adoptRoster(c.roster);
        break;
      case 'act':
        if (!this.transport.isHost || !s) break;
        if (c.action === 'target') s.acceptTarget(from, c.target ?? null);
        else s.acceptAction(from, c.action);
        break;
      case 'lobby': {
        if (!this.transport.isHost) {
          // Travelling this direction a lobby message means exactly one thing.
          // Names, teams and readiness come back down inside the roster, which
          // is already the single description of who is in the room.
          if (c.start) this.opts.onStart?.();
          break;
        }
        const e = this.roster.find((r) => r.id === from);
        if (!e) break;
        if (c.name) e.name = c.name.slice(0, 18);
        if (c.team) e.team = c.team;
        if (c.ready !== undefined) e.ready = c.ready;
        this.publishRoster();
        break;
      }
      case 'event':
        if (!this.transport.isHost) s?.applyNetEvent(c.e);
        break;
      case 'ping':
        this.send(from, encodeControl({ t: 'pong', at: c.at, hostTime: now() }));
        break;
      case 'pong': {
        const ms = Math.max(0, now() - c.at);
        this.rtt.set(from, ms);
        const e = this.roster.find((r) => r.id === from);
        if (e) e.ping = Math.round(ms);
        break;
      }
    }
  }

  // --- roster ------------------------------------------------------------

  private onJoin(_id: PeerId): void {
    if (this.transport.isHost) return; // A client is admitted when it says hello.
    // We are the client and the host just became reachable.
    this.hello();
  }

  private onLeave(id: PeerId, _reason: string): void {
    const i = this.roster.findIndex((r) => r.id === id);
    if (i >= 0) this.roster.splice(i, 1);
    this.rtt.delete(id);
    this.session?.removePlayer(id);
    if (this.transport.isHost) this.publishRoster();
    else this.opts.onRoster?.(this.roster);
  }

  /** Host: give a newcomer a slot, a body, and the state of the room. */
  private admit(id: PeerId, name: string): void {
    if (this.roster.some((r) => r.id === id)) return;
    const slot = this.freeSlot();
    if (slot < 0) {
      this.send(
        id,
        encodeControl({ t: 'event', e: { t: 'toast', text: 'LOBBY FULL', tone: 'bad' } }),
      );
      return;
    }
    const entry: RosterEntry = {
      id,
      slot,
      name: (name || `Crew ${slot}`).slice(0, 18),
      team: 'thief',
      ready: false,
      ping: 0,
      host: false,
    };
    this.roster.push(entry);
    this.session?.addPlayer(id, slot, entry.name, entry.team);
    this.send(
      id,
      encodeControl({
        t: 'welcome',
        you: id,
        slot,
        hostTime: now(),
        roster: this.roster,
        phase: 0,
      }),
    );
    this.publishRoster();
  }

  private applyWelcome(c: Extract<Control, { t: 'welcome' }>): void {
    this.adoptRoster(c.roster);
    this.opts.onWelcome?.({ you: c.you, slot: c.slot, roster: c.roster });
  }

  /**
   * Take the host's word for the room.
   *
   * Replacing the array wholesale would be simpler, but the lobby UI holds a
   * reference to it, so it is mutated in place. Anyone who has appeared since we
   * last looked gets a body; anyone who has gone loses theirs.
   */
  private adoptRoster(next: readonly RosterEntry[]): void {
    const seen = new Set<string>();
    for (const r of next) {
      seen.add(r.id);
      const mine = this.roster.find((x) => x.id === r.id);
      if (mine) Object.assign(mine, r);
      else this.roster.push({ ...r });
      if (this.session && r.id !== this.session.selfId) {
        this.session.addPlayer(r.id, r.slot, r.name, r.team);
      }
    }
    for (let i = this.roster.length - 1; i >= 0; i--) {
      if (seen.has(this.roster[i].id)) continue;
      const [gone] = this.roster.splice(i, 1);
      this.session?.removePlayer(gone.id);
    }
    this.roster.sort((a, b) => a.slot - b.slot);
    this.host = this.roster.find((r) => r.host)?.id ?? this.host;
    this.opts.onRoster?.(this.roster);
  }

  private publishRoster(): void {
    this.roster.sort((a, b) => a.slot - b.slot);
    this.opts.onRoster?.(this.roster);
    if (this.transport.peers.length) this.transport.broadcast(encodeControl({ t: 'roster', roster: this.roster }));
  }

  private freeSlot(): number {
    for (let i = 0; i < MAX_PLAYERS; i++) if (!this.roster.some((r) => r.slot === i)) return i;
    return -1;
  }

  // --- plumbing ----------------------------------------------------------

  private hello(): void {
    this.control({ t: 'hello', name: this.opts.name, version: PROTOCOL_VERSION });
  }

  /** To the host if we are a client, to everybody if we are the host. */
  private control(c: Control): void {
    const text = encodeControl(c);
    if (this.transport.isHost) {
      if (this.transport.peers.length) this.transport.broadcast(text);
    } else {
      this.send(this.hostId(), text);
    }
  }

  private send(to: PeerId, data: ArrayBuffer | string): void {
    if (!to) return;
    this.transport.send(to, data);
  }

  /**
   * Where a client sends everything.
   *
   * Before the roster lands there is only one peer and it has to be the host, so
   * the guess is safe. After it lands the answer is known, and it stops being a
   * guess on a transport that puts every peer in everyone's list — which the
   * loopback switch does, and Steam's relay will too.
   */
  private hostId(): PeerId {
    return this.host || this.transport.peers[0] || '';
  }
}

function versionToast(theirs: number): NetEvent {
  return {
    t: 'toast',
    text: `VERSION MISMATCH — HOST ${PROTOCOL_VERSION}, YOU ${theirs}`,
    tone: 'bad',
  };
}

/**
 * Milliseconds, monotonic where the platform offers it.
 *
 * Node has `performance` too, but the QA harness runs some of this in contexts
 * that stub the global away, and a ping that throws is a lobby that never forms.
 */
function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
